import type {
    ConfigurableProductFilterObject,
    ConfigurableProductFilterOptionObject,
    ConfigurableProductSetDiscountObject,
    ConfigurableProductSetOptionObject,
    ConfigurableProductSetPositionObject,
} from '@/types/generated';
import _ from 'lodash';

/** Prices arrive in the store's currency (major units); sums are done in minor units so they match the server. */
export function toCents(amount: number): number {
    return Math.round(amount * 100);
}

export function fromCents(cents: number): number {
    return cents / 100;
}

/** Keyed by list index, not by `position`, which is not guaranteed to be contiguous or start at one. */
export function setPositionFilterName(index: number): string {
    return `set_position_${index}`;
}

/**
 * Dress a set option up as a filter option so FilterSelectMenu can render it
 * unchanged. `value` is the SKU because the menu compares options by it.
 */
export function setOptionToFilterOption(option: ConfigurableProductSetOptionObject): ConfigurableProductFilterOptionObject {
    return {
        label: option.label ?? option.name ?? option.sku,
        value: option.sku,
        is_default: option.is_default,
        product_price: option.price,
        product_price_recommended: null,
        default_sku: option.sku,
    };
}

/** Small to large; an option without dimensions cannot be placed and goes last. */
function optionSizeOrder(option: ConfigurableProductSetOptionObject): number {
    return option.width && option.height ? option.width * option.height : Number.MAX_SAFE_INTEGER;
}

/**
 * One dropdown per position. `predefined_show` stays null so the "most chosen"
 * badge never renders — a set carries no popularity signal to base it on.
 */
export function setPositionToFilter(position: ConfigurableProductSetPositionObject, index: number, label: string): ConfigurableProductFilterObject {
    return {
        meta: {
            label,
            name: setPositionFilterName(index),
            description: null,
            predefined_show: null,
            predefined_show_text: null,
            hide_price_in_dropdown: false,
        },
        type: null,
        // The menu keys its options by value, so a SKU repeated inside one
        // position would produce duplicate keys.
        options: _.sortBy(
            _.uniqBy(position.options, (option: ConfigurableProductSetOptionObject) => option.sku),
            optionSizeOrder,
        ).map(setOptionToFilterOption),
    };
}

/**
 * A position and the dropdown that drives it, paired so the template can render the two together
 * without indexing two lists in step.
 */
export type SetPositionColumn = {
    filter: ConfigurableProductFilterObject;
    image: ConfigurableProductSetPositionObject['image'];
};

export function defaultSetOption(position: ConfigurableProductSetPositionObject): ConfigurableProductSetOptionObject | undefined {
    return _.find(position.options, (option: ConfigurableProductSetOptionObject) => option.is_default) ?? _.first(position.options);
}

export function findSetOption(
    position: ConfigurableProductSetPositionObject,
    sku: string | undefined,
): ConfigurableProductSetOptionObject | undefined {
    return _.find(position.options, (option: ConfigurableProductSetOptionObject) => option.sku === sku);
}

/**
 * Mirrors SetPriceCalculationService in Grutte Apier: a percentage is capped at
 * 100, an absolute amount at the subtotal, and neither can go negative. The two
 * must agree to the cent or the price on the product page will not match the
 * price in the cart.
 */
export function setDiscountCents(subtotalCents: number, discount: ConfigurableProductSetDiscountObject | null | undefined): number {
    if (!discount || subtotalCents <= 0) {
        return 0;
    }

    if (discount.is_percentage) {
        return Math.round((subtotalCents * _.clamp(discount.amount, 0, 100)) / 100);
    }

    return _.clamp(toCents(discount.amount), 0, subtotalCents);
}
